import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useRef, useEffect, useState } from 'react';
import { FeaturedEventItem } from 'api/responseTypes';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Calendar, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import Carousel, { type ControlProps } from 'nuka-carousel';
import { useFeaturedEventsQuery } from 'pages/HomePage/hooks/useFeaturedEvents';
import { useEventTracking } from 'hooks/useEventTracking';

type CarouselControlsRef = Pick<ControlProps, 'goToSlide' | 'nextSlide' | 'previousSlide'>;

const MAX_FEATURED_EVENTS = 3;
const AUTOPLAY_INTERVAL_MS = 5000;

const getEventKey = (item: FeaturedEventItem): string | null => {
  if (item.match) return item.match.eventKey;
  if (item.concert) return item.concert.eventKey;
  return null;
};

const getEventDetails = (
  item: FeaturedEventItem,
): {
  subtitle: string | null;
  mainTitle: string;
  date: string | null;
  time: string;
  venue: string;
  startingPrice: number;
  currency: string;
  isFreeEvent: boolean;
} => {
  if (item.match) {
    const subtitle = [item.match.title, item.match.subtitle].filter(Boolean).join(' - ');
    const mainTitle = `${item.match.teamA.name} vs ${item.match.teamB.name}`;
    return {
      subtitle: subtitle || null,
      mainTitle,
      date: item.match.date,
      time: item.match.time,
      venue: item.match.stadium ?? item.match.city,
      startingPrice: item.match.startingPrice,
      currency: item.match.currency,
      isFreeEvent: Boolean(item.match.isFreeEvent),
    };
  }
  if (item.concert) {
    const subtitle = item.concert.subtitle || null;
    return {
      subtitle,
      mainTitle: item.concert.title,
      date: item.concert.date,
      time: item.concert.time,
      venue: item.concert.stadium ?? item.concert.city,
      startingPrice: item.concert.startingPrice,
      currency: item.concert.currency,
      isFreeEvent: Boolean(item.concert.isFreeEvent),
    };
  }
  return {
    subtitle: null,
    mainTitle: '',
    date: null,
    time: '',
    venue: '',
    startingPrice: 0,
    currency: '',
    isFreeEvent: false,
  };
};

const getImageSrc = (item: FeaturedEventItem): string | null => {
  if (item.featuredEventImage) return item.featuredEventImage;
  if (item.concert?.imageLink) return item.concert.imageLink;
  return null;
};

interface FeaturedEventSlideProps {
  item: FeaturedEventItem;
  slideIndex: number;
  currentSlide: number;
  slideCount: number;
  onGoToSlide: (index: number) => void;
}

const FeaturedEventSlide = ({
  item,
  slideIndex,
  currentSlide,
  slideCount,
  onGoToSlide,
}: FeaturedEventSlideProps) => {
  const navigate = useNavigate();
  const { eventTracking } = useEventTracking();
  const eventKey = getEventKey(item);
  const { subtitle, mainTitle, date, time, venue, startingPrice, currency, isFreeEvent } =
    getEventDetails(item);
  const imageSrc = getImageSrc(item);

  const handleBookNow = () => {
    if (!eventKey) return;

    const eventId = item.match?.eventId ?? item.concert?.eventId;
    eventTracking.event.clicked({
      eventId,
      eventName: mainTitle,
      eventDate: date,
      venue: venue || undefined,
      source: 'home_page',
    });

    if (item.concert) {
      navigate('/concert/' + eventKey);
    } else {
      navigate('/event/' + eventKey);
    }
  };

  const dateTimeParts = [date, time].filter(Boolean);
  const dateTimeText = dateTimeParts.length > 0 ? dateTimeParts : null;
  const formattedPriceNumber =
    !isFreeEvent && startingPrice > 0 ? new Intl.NumberFormat().format(startingPrice) : null;

  return (
    <>
      {/* ── Desktop slide ── */}
      <Box
        display={{ base: 'none', md: 'block' }}
        position='relative'
        width='100%'
        h={{ md: '520px', lg: '70vh' }}
        maxH='750px'
        overflow='hidden'
        bg='dark.D900'
        bgImage={imageSrc ? `url(${imageSrc})` : undefined}
        bgSize='cover'
        bgPos='center'
        bgRepeat='no-repeat'
      >
        <Box
          position='absolute'
          inset={0}
          bgGradient='linear(to-r, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.75) 100%)'
        />

        <Flex
          position='absolute'
          top={0}
          right={0}
          bottom={0}
          width='50%'
          align='center'
          justify='center'
          dir='rtl'
        >
          <Flex
            direction='column'
            gap={3}
            align='flex-start'
            textAlign='right'
            w='100%'
            pl={{ md: 10, lg: 14 }}
            pr={{ md: 20, lg: 24 }}
          >
            {subtitle && (
              <Text fontSize='14px' color='white'>
                {subtitle}
              </Text>
            )}
            {mainTitle && (
              <Heading
                as='h3'
                fontSize={{ md: '28px', lg: '36px' }}
                fontWeight='600'
                color='white'
                lineHeight='1.2'
              >
                {mainTitle}
              </Heading>
            )}

            <Flex direction='column' gap={1.5} mt={2}>
              {dateTimeText && (
                <Flex align='center' gap={2}>
                  <Icon as={Calendar} boxSize='16px' color='white' />
                  <Text fontSize='14px' color='white'>
                    {dateTimeText[0]}
                    {dateTimeText.length > 1 && (
                      <>
                        <Text as='span' fontWeight='900' fontSize='16px' mx={3}>
                          ·
                        </Text>
                        {dateTimeText[1]}
                      </>
                    )}
                  </Text>
                </Flex>
              )}
              {venue && (
                <Flex align='center' gap={2}>
                  <Icon as={MapPin} boxSize='16px' color='white' />
                  <Text fontSize='14px' color='white' noOfLines={1}>
                    {venue}
                  </Text>
                </Flex>
              )}
            </Flex>

            {isFreeEvent && (
              <Flex mt={2.5}>
                <Badge
                  bg='teal.500'
                  color='white'
                  px={4}
                  py={2}
                  borderRadius='full'
                  fontSize='md'
                  fontWeight='600'
                  textTransform='none'
                >
                  <FormattedMessage id='freeEvent.label' defaultMessage='Free' />
                </Badge>
              </Flex>
            )}
            {formattedPriceNumber && (
              <Flex direction='column' align='flex-start' gap={0.5} mt={2.5}>
                <Text fontSize='16px' color='whiteAlpha.800'>
                  <FormattedMessage defaultMessage='ابتداءً من' id='featured.from' />
                </Text>
                <Text fontSize='32px' fontWeight='600' color='white' lineHeight='1.1'>
                  {formattedPriceNumber}{' '}
                  <Text as='span' fontSize='18px' fontWeight='400'>
                    {currency}
                  </Text>
                </Text>
              </Flex>
            )}
            <Button
              variant='primaryLarge'
              size='lg'
              onClick={handleBookNow}
              aria-label='احجز الآن'
              mt={3}
            >
              <FormattedMessage defaultMessage='احجز الآن' id='featured.bookNow' />
            </Button>
          </Flex>
        </Flex>

        {/* Desktop dots – over the image */}
        {slideCount > 1 && currentSlide === slideIndex && (
          <Flex
            gap={2}
            justify='center'
            role='tablist'
            aria-label='Carousel navigation'
            position='absolute'
            bottom={5}
            left='50%'
            transform='translateX(-50%)'
            zIndex={10}
            display={{ base: 'none', md: 'flex' }}
          >
            {Array.from({ length: slideCount }).map((_, index) => (
              <Box
                key={index}
                as='button'
                type='button'
                role='tab'
                aria-selected={currentSlide === index}
                aria-label={`Slide ${index + 1} of ${slideCount}`}
                w={currentSlide === index ? 8 : 2}
                h={2}
                borderRadius='full'
                bg={currentSlide === index ? 'white' : 'whiteAlpha.500'}
                transition='all 0.2s'
                cursor='pointer'
                onClick={() => onGoToSlide(index)}
                _hover={{ bg: currentSlide === index ? 'white' : 'whiteAlpha.700' }}
              />
            ))}
          </Flex>
        )}
      </Box>

      {/* ── Mobile slide: image with title overlay + dark details panel ── */}
      <Box display={{ base: 'block', md: 'none' }} bg='#0D0D0D'>
        {/* Image area with subtitle + title only */}
        <Box
          position='relative'
          width='100%'
          sx={{ aspectRatio: '393 / 207' }}
          bg='#0D0D0D'
          bgImage={imageSrc ? `url(${imageSrc})` : undefined}
          bgSize='cover'
          bgPos='center'
          bgRepeat='no-repeat'
        >
          <Box
            position='absolute'
            inset={0}
            bgGradient='linear(to-t, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.7) 25%, rgba(0,0,0,0) 100%)'
          />
          <Flex
            position='absolute'
            bottom={4}
            left={0}
            right={0}
            direction='column'
            gap={1}
            px={5}
            textAlign='right'
            dir='rtl'
          >
            {subtitle && (
              <Text fontSize='13px' color='whiteAlpha.900'>
                {subtitle}
              </Text>
            )}
            {mainTitle && (
              <Heading as='h3' fontSize='20px' fontWeight='600' color='white' lineHeight='1.3'>
                {mainTitle}
              </Heading>
            )}
          </Flex>
        </Box>

        {/* Details on solid dark background */}
        <Flex direction='column' gap={2} bg='#0D0D0D' px={5} py={4} textAlign='right' dir='rtl'>
          <Flex direction='column' gap={1.5}>
            {dateTimeText && (
              <Flex align='center' gap={2}>
                <Icon as={Calendar} boxSize='16px' color='white' />
                <Text fontSize='14px' color='white'>
                  {dateTimeText[0]}
                  {dateTimeText.length > 1 && (
                    <>
                      <Text as='span' fontWeight='900' fontSize='16px' mx={3}>
                        ·
                      </Text>
                      {dateTimeText[1]}
                    </>
                  )}
                </Text>
              </Flex>
            )}
            {venue && (
              <Flex align='center' gap={2}>
                <Icon as={MapPin} boxSize='16px' color='white' />
                <Text fontSize='14px' color='white' noOfLines={1}>
                  {venue}
                </Text>
              </Flex>
            )}
          </Flex>

          {isFreeEvent && (
            <Flex mt={2}>
              <Badge
                bg='teal.500'
                color='white'
                px={3}
                py={1.5}
                borderRadius='full'
                fontSize='sm'
                fontWeight='600'
                textTransform='none'
              >
                <FormattedMessage id='freeEvent.label' defaultMessage='Free' />
              </Badge>
            </Flex>
          )}
          {formattedPriceNumber && (
            <Flex direction='column' align='flex-start' gap={0.5} mt={2}>
              <Text fontSize='16px' color='whiteAlpha.800'>
                <FormattedMessage defaultMessage='ابتداءً من' id='featured.from' />
              </Text>
              <Text fontSize='20px' fontWeight='600' color='#80C3BA' lineHeight='1.1'>
                {formattedPriceNumber}{' '}
                <Text as='span' fontSize='14px' fontWeight='400'>
                  {currency}
                </Text>
              </Text>
            </Flex>
          )}
          <Button
            variant='primaryLarge'
            size='lg'
            onClick={handleBookNow}
            aria-label='احجز الآن'
            mt={2}
            width='100%'
          >
            <FormattedMessage defaultMessage='احجز الآن' id='featured.bookNow' />
          </Button>
        </Flex>

        {/* Mobile dots – below the bottom of content */}
        {slideCount > 1 && (
          <Flex
            gap={2}
            justify='center'
            role='tablist'
            aria-label='Carousel navigation'
            bg='#0D0D0D'
            pt={4}
            pb={5}
            display='flex'
          >
            {Array.from({ length: slideCount }).map((_, index) => (
              <Box
                key={index}
                as='button'
                type='button'
                role='tab'
                aria-selected={currentSlide === index}
                aria-label={`Slide ${index + 1} of ${slideCount}`}
                w={currentSlide === index ? 8 : 2}
                h={2}
                borderRadius='full'
                bg={currentSlide === index ? 'white' : 'whiteAlpha.500'}
                transition='all 0.2s'
                cursor='pointer'
                onClick={() => onGoToSlide(index)}
                _hover={{ bg: currentSlide === index ? 'white' : 'whiteAlpha.700' }}
              />
            ))}
          </Flex>
        )}
      </Box>
    </>
  );
};

export const FeaturedEventsCarousel = () => {
  const { isLoading, items, isError } = useFeaturedEventsQuery();
  const displayItems = items.slice(0, MAX_FEATURED_EVENTS);
  const showArrows = useBreakpointValue({ base: false, md: true });
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideCount = displayItems.length;
  const carouselControlsRef = useRef<CarouselControlsRef | null>(null);

  useEffect(() => {
    setCurrentSlide((s) => Math.min(s, Math.max(0, slideCount - 1)));
  }, [slideCount]);

  if (isLoading) {
    return null;
  }

  if (isError || slideCount === 0) {
    return null;
  }

  const handlePrevSlide = () => {
    carouselControlsRef.current?.previousSlide();
  };

  const handleNextSlide = () => {
    carouselControlsRef.current?.nextSlide();
  };

  return (
    <Box width='100%' overflow='hidden' dir='ltr' fontFamily="'Noto Sans Arabic', sans-serif">
      <Box position='relative' width='100%'>
        <Carousel
          afterSlide={setCurrentSlide}
          autoplay
          wrapAround
          autoplayInterval={AUTOPLAY_INTERVAL_MS}
          slidesToShow={1}
          slidesToScroll={1}
          dragging
          swiping
          pauseOnHover
          renderCenterLeftControls={(props) => {
            carouselControlsRef.current = {
              goToSlide: props.goToSlide,
              nextSlide: props.nextSlide,
              previousSlide: props.previousSlide,
            };
            return null;
          }}
          renderCenterRightControls={() => null}
          renderBottomCenterControls={() => null}
          renderTopCenterControls={() => null}
        >
          {displayItems.map((item: FeaturedEventItem, index) => (
            <FeaturedEventSlide
              key={item.id}
              item={item}
              slideIndex={index}
              currentSlide={currentSlide}
              slideCount={slideCount}
              onGoToSlide={(idx) => carouselControlsRef.current?.goToSlide(idx)}
            />
          ))}
        </Carousel>

        {/* Arrows – desktop only */}
        {showArrows && slideCount > 1 && (
          <>
            <Box
              as='button'
              type='button'
              onClick={handlePrevSlide}
              aria-label='Previous slide'
              position='absolute'
              left={5}
              top='50%'
              transform='translateY(-50%)'
              w={10}
              h={10}
              borderRadius='full'
              bg='#FFFFFFE5'
              color='dark.D900'
              display='flex'
              alignItems='center'
              justifyContent='center'
              _hover={{ bg: 'white' }}
              zIndex={10}
            >
              <ChevronLeft size={20} />
            </Box>

            <Box
              as='button'
              type='button'
              onClick={handleNextSlide}
              aria-label='Next slide'
              position='absolute'
              right={5}
              top='50%'
              transform='translateY(-50%)'
              w={10}
              h={10}
              borderRadius='full'
              bg='#FFFFFFE5'
              color='dark.D900'
              display='flex'
              alignItems='center'
              justifyContent='center'
              _hover={{ bg: 'white' }}
              zIndex={10}
            >
              <ChevronRight size={20} />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};
